import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TabsComponent from '@shared/components/tabs/tabs';
import { SeriesDialogFormProps } from '@modules/entertainment/models/series.model';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import GenreAddDialog from '@modules/entertainment/components/genre-add-dialog/genre-add-dialog';
import { GenresEnum } from '@modules/entertainment/models/entertainment.models';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog';
import { LanguageEnum } from '@shared/models/language.model';
import dayjs from 'dayjs';
import { CrudActionEnum } from '@models/app-autocomplete.model.ts';
import useLanguageStore from '@store/language-store/language.store';
import { validateNotOnlySpaces } from '@shared/utils/trim-form-values-utils';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import useSeriesStore from '@modules/entertainment/pages/series/store/series.store';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import { seriesErrorMessages } from '@modules/entertainment/utils/series.utils';
import MediaLibraryComponent from '@shared/components/media-library-component/media-library-component';
import { FileTypeEnum } from '@shared/models/media-library.model';
import { useErrorMessage } from '@shared/hooks/use-error-message';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AppAutocompleteWithActions from '@shared/components/app-autocomplete-with-actions/app-autocomplete-with-actions';

const SeriesDialogForm: React.FC<SeriesDialogFormProps> = ({
  selectedItem,
  handleClose,
  onSave
}) => {
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    trigger
  } = useForm<any>({ mode: 'onTouched' });

  const { t } = useTranslation();
  const { languageList } = useLanguageStore((state) => state);
  const getErrorMessage = useErrorMessage();

  const { resetSelectedMedia } = useMediaLibraryStore();
  const {
    isLoading,
    createGenre,
    editGenre,
    deleteGenre,
    isGenreLoading,
    genreList
  } = useSeriesStore();

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showAddNewGenreDialog, setShowAddNewGenreDialog] = useState(false);
  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = useState<string>(defaultLang);
  const [genresLists, setGenresLists] = useState<any[]>([]);
  const [selectedGenreValue, setSelectedGenreValue] = useState<any>(null);
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const [multiLangFields, setMultiLangFields] = useState<{
    title: Record<string, string | undefined>;
    description: Record<string, string | undefined>;
  }>({
    title: {},
    description: {}
  });

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentTitle = watch('title');
    const currentDescription = watch('description');

    setMultiLangFields((prev) => ({
      title: { ...prev.title, [currentTab]: currentTitle },
      description: { ...prev.description, [currentTab]: currentDescription }
    }));

    setCurrentTab(newLang);
    setValue('title', multiLangFields.title?.[newLang] || '');
    setValue('description', multiLangFields.description?.[newLang] || '');
  };

  const onSubmit = (data: any) => {
    const currentTitle = watch('title');
    const currentDescription = watch('description');

    const fullData: any = {
      title: { ...multiLangFields.title, [currentTab]: currentTitle },
      description: {
        ...multiLangFields.description,
        [currentTab]: currentDescription
      },
      date: data.date,
      backdrop: selectedMedia.backdropImage?.path || null,
      poster: selectedMedia.posterImage?.path || null,
      genres: genresLists.map(({ id }) => id)
    };

    onSave(fullData);
  };

  const handleSaveGenre = async (value: any) => {
    if (selectedGenreValue?.id) {
      editGenre({
        data: value,
        id: selectedGenreValue?.id,
        handleClose: handleCloseGenreDialog
      });
    } else {
      createGenre({
        data: value,
        handleClose: handleCloseGenreDialog
      });
    }
  };

  const handleDeleteConfirm = async () => {
    deleteGenre({
      id: selectedGenreValue?.id,
      handleClose: () => setShowDeleteDialog(false)
    });
  };

  const handleCloseGenreDialog = () => {
    resetSelectedMedia('genreImage');
    setShowAddNewGenreDialog(false);
    setShowDeleteDialog(false);
    setSelectedGenreValue(null);
  };

  const itemActionHandler = (value: any, type: CrudActionEnum) => {
    if (type === CrudActionEnum.Add) {
      setSelectedGenreValue({ name: { [currentTab]: value } });
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Edit) {
      setSelectedGenreValue(value);
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Delete) {
      setSelectedGenreValue(value);
      setShowDeleteDialog(true);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const { title, description } = selectedItem;

      const date = selectedItem.date ? dayjs(selectedItem.date) : null;

      setMultiLangFields({ title, description });

      const genreObjects =
        selectedItem.genres?.map((genre: any) => ({
          ...genre,
          id: genre._id,
          name: genre.name || Object.values(genre.name)[0]
        })) || [];

      reset({
        title: title[defaultLang] || '',
        description: description[defaultLang] || '',
        date: date,
        genres: genreObjects
      });

      setGenresLists(genreObjects);
    }
  }, [defaultLang, selectedItem, reset]);

  return (
    <div>
      <div className="w-full mb-7 mt-7">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>
      <form className="flex flex-col gap-1">
        <div className="flex gap-4 mb-[15px]">
          <div className="media-wrapper">
            <div className="flex mb-1.5">
              {t('label_backdrop')}
              <InfoTooltip text="tooltip_info_series_backdrop" />
            </div>
            <MediaLibraryComponent
              width={240}
              height={120}
              mediaKey={'backdropImage'}
              allowedTypes={[FileTypeEnum.Image]}
              path={selectedItem?.backdrop}
              onSelect={(media) =>
                setSelectedMedia((prev: any) => ({
                  ...prev,
                  backdropImage: media
                }))
              }
            />
          </div>

          <div className="media-wrapper">
            <div className="flex mb-1.5">
              {t('label_poster')}
              <InfoTooltip text="tooltip_info_series_poster" />
            </div>
            <MediaLibraryComponent
              width={240}
              height={120}
              mediaKey={'posterImage'}
              allowedTypes={[FileTypeEnum.Image]}
              path={selectedItem?.poster}
              onSelect={(media) =>
                setSelectedMedia((prev: any) => ({
                  ...prev,
                  posterImage: media
                }))
              }
            />
          </div>
        </div>
        <div className="w-full flex gap-7">
          <div className="w-full flex flex-col gap-1">
            <FormControl error={!!errors.title}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_title')}
                <InfoTooltip text="tooltip_info_series_title" />
              </FormLabel>

              <OutlinedInput
                {...register('title', {
                  required: true,
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces
                  }
                })}
                error={!!errors.title}
                placeholder={t('placeholder_series_title')}
              />
              <FormHelperText>
                {errors?.title &&
                  getErrorMessage({
                    errors,
                    fieldName: 'title',
                    errorMessages: seriesErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <FormControl error={!!errors.description}>
              <FormLabel className="flex">
                {t('label_description')}
                <InfoTooltip text="tooltip_info_series_description" />
              </FormLabel>

              <OutlinedInput
                multiline
                rows={4}
                {...register('description', {
                  required: false
                })}
                placeholder={t('placeholder_series_description')}
              />
              <FormHelperText>
                {errors?.description &&
                  getErrorMessage({
                    errors,
                    fieldName: 'description',
                    errorMessages: seriesErrorMessages
                  })}
              </FormHelperText>
            </FormControl>
          </div>

          <div className="w-full flex flex-col gap-4">
            <div className="w-full flex flex-col gap-4">
              <FormControl className="w-full" error={!!errors.date}>
                <FormLabel className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_date')}
                  <InfoTooltip text="tooltip_info_movies_date_range" />
                </FormLabel>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="date"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        value={field.value || null}
                        onChange={(newValue) => field.onChange(newValue)}
                        onClose={() => {
                          trigger('date');
                        }}
                        slotProps={{
                          textField: {
                            error: !!errors.date,
                            onBlur: async (event: any) => {
                              if (
                                event.relatedTarget?.closest('[role="dialog"]')
                              )
                                return;

                              field.onBlur();
                              await trigger('date');
                            }
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>

                <FormHelperText>
                  {errors?.date &&
                    getErrorMessage({
                      errors,
                      fieldName: 'date',
                      errorMessages: seriesErrorMessages
                    })}
                </FormHelperText>
              </FormControl>
            </div>

            <AppAutocompleteWithActions
              placeholder={t('placeholder_series_genres')}
              name="genres"
              control={control}
              label={
                <span className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_genres')}
                  <InfoTooltip text="tooltip_info_series_genres" />
                </span>
              }
              options={genreList}
              selectedList={genresLists}
              onChange={setGenresLists}
              itemActionHandler={itemActionHandler}
              validationProps={{
                errors,
                errorMessages: seriesErrorMessages,
                getErrorMessage,
                validationRules: { required: true }
              }}
              language={currentTab}
            />
          </div>
        </div>

        <div className="flex justify-end items-center gap-6 pt-4 pb-1 sticky bottom-0 bg-white">
          <Button
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            loading={isLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>
      <AppDialog
        open={showAddNewGenreDialog}
        title={t('entertainment_add_genre')}
        content={
          <GenreAddDialog
            selectedValue={selectedGenreValue}
            type={GenresEnum.Series}
            handleSave={handleSaveGenre}
            loading={isGenreLoading()}
          />
        }
        onClose={handleCloseGenreDialog}
      />
      <div className="w-full h-full">
        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          isLoading={isGenreLoading()}
          open={showDeleteDialog}
          title={t('delete_popup_title')}
        />
      </div>
    </div>
  );
};

export default SeriesDialogForm;
